import { getAccessToken } from '@/utils/storage';
import { getAnalyticsSessionId, getAnalyticsVisitorId } from '@/utils/analyticsIdentity';

type AnalyticsUser = {
  userId?: string;
  role?: string;
} | null;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
const sentVisitKeys = new Set<string>();
const EXCLUDED_ANALYTICS_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  'preview.tutefounder.com',
  'preview.tutorfounder.com',
]);

const buildEndpoint = (path: string): string => {
  const base = API_BASE_URL?.replace(/\/$/, '') ?? '';
  return `${base}${path}`;
};

const getDeviceType = (): 'mobile' | 'desktop' | 'tablet' => {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad/.test(ua)) return 'tablet';
  if (/mobile|android|iphone/.test(ua)) return 'mobile';
  return 'desktop';
};

const getBrowser = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'Edge';
  if (ua.includes('chrome/') && !ua.includes('chromium')) return 'Chrome';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('opr/') || ua.includes('opera/')) return 'Opera';
  return 'Other';
};

const getPath = (): string => `${window.location.pathname}${window.location.search}`;

const shouldSkipAnalytics = (): boolean => {
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'localhost'
    || hostname.endsWith('.localhost')
    || EXCLUDED_ANALYTICS_HOSTNAMES.has(hostname);
};

const getUtm = (): { utmSource?: string; utmMedium?: string; utmCampaign?: string } => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
  };
};

const sendAnalyticsPayload = (endpoint: string, payload: Record<string, unknown>): void => {
  const token = getAccessToken();
  const url = buildEndpoint(endpoint);
  const body = JSON.stringify(payload);

  if (!token && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon(url, blob)) return;
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    keepalive: true,
  }).catch(() => {
    // Analytics must never interrupt the user journey.
  });
};

export const trackSiteVisitStarted = (user: AnalyticsUser): void => {
  if (shouldSkipAnalytics()) return;

  const sessionId = getAnalyticsSessionId();
  const visitorId = getAnalyticsVisitorId();
  const visitKey = `${sessionId}:${user?.userId ?? 'anonymous'}`;

  if (sentVisitKeys.has(visitKey)) return;
  sentVisitKeys.add(visitKey);

  sendAnalyticsPayload('/analytics/site-visit', {
    sessionId,
    visitorId,
    enteredAt: new Date().toISOString(),
    landingPath: getPath(),
    referrer: document.referrer || undefined,
    ...getUtm(),
    deviceType: getDeviceType(),
    browser: getBrowser(),
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenSize: `${window.screen.width}x${window.screen.height}`,
  });
};

export const trackSitePageViewed = (referrer?: string): void => {
  if (shouldSkipAnalytics()) return;

  sendAnalyticsPayload('/analytics/page-view', {
    sessionId: getAnalyticsSessionId(),
    visitorId: getAnalyticsVisitorId(),
    path: getPath(),
    title: document.title,
    referrer,
    viewedAt: new Date().toISOString(),
  });
};
